import { Component, Input, OnDestroy, OnInit, SimpleChanges } from '@angular/core';

@Component({
  selector: 'app-progress-bar-block-ui',
  templateUrl: './progress-bar-block-ui.component.html',
  styleUrls: ['./progress-bar-block-ui.component.scss']
})
export class ProgressBarBlockUiComponent implements OnInit, OnDestroy {
  @Input() isLoading: boolean = false;
  @Input() message: string = 'Procesando...';
  @Input() showCancelButton: boolean = false;
  @Input() showPercentage: boolean = true;
  @Input() variant: 'default' | 'slim' | 'transparent' | 'dark' = 'default';

  @Input() onCancel?: () => void;

  @Input() progressValue = 0;
  @Input() animateProgress = false;
  @Input() animationSpeed = 2;
  @Input() animationInterval: any;

  // Nuevas propiedades para controlar el comportamiento
  @Input() maxProgress1min = 70; // Máximo a los 1 minuto
  @Input() maxProgressFinal = 90; // Máximo final
  @Input() incrementAfter1min = 0.5; // Incremento por segundo después de 1min
  
  // Propiedades para el círculo de progreso
  radius = 100;
  circumference = 2 * Math.PI * this.radius;
  completedDashOffset = this.circumference;
  
  // Estado interno
  private isFirst1min = true;
  private oneMinuteMarkReached = false;
  private lastUpdateTime = 0;

  constructor() { }

  ngOnDestroy(): void {
    this.stopAnimation()
  }

  ngOnInit() {
    this.updateProgress();
    // Iniciar animación automáticamente si está habilitada
    if (this.animateProgress) {
      setTimeout(() => {
        this.startAnimation();
      }, 500);
    }
  }

  get containerClass(): string {
    return `progress-overlay-container ${this.variant}`;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['progressValue']) {
      this.updateFromEndpoint(this.progressValue);
    }

    if (changes['animateProgress']) {
      this.toggleAnimation()
    }
  }
  
  cancel() {
    if (this.onCancel) {
      this.onCancel();
    }
  }

  private toggleAnimation(): void {
    if (this.animateProgress) {
      this.startAnimation();
    } else {
      this.stopAnimation();
    }
  }

  completeProgress(): void {
    this.stopAnimation();
    setTimeout(() => {
      this.progressValue = 100;
    }, 500);
    
    this.updateProgress();
    this.progressValue = 0;
  }

  updateProgress(): void {
    // Calcular el offset para el progreso completado
    const progress = this.progressValue / 100;
    this.completedDashOffset = this.circumference * (1 - progress);
  }

  formatLabel(value: number): string {
    return `${Math.round(value)}%`;
  }

  startAnimation(): void {
    this.stopAnimation(); // Detener cualquier animación existente
    
    // Configuración de tiempos - 70% en 1 minuto
    const firstPhaseDuration = 1 * 60 * 1000; // 1 minuto en milisegundos (60,000 ms)
    
    // Usar el animationSpeed para ajustar la frecuencia de actualización
    const intervalTime = 1000 / this.animationSpeed; // Mantener tu configuración existente
    
    // Guardar tiempo de inicio y progreso inicial
    const startTime = Date.now();
    const startProgress = this.progressValue;
    
    // Calcular velocidad para primera fase (hasta 70% en 1min)
    const progressTo70 = this.maxProgress1min - startProgress;
    const incrementPerMsFirstPhase = progressTo70 / firstPhaseDuration;
    
    this.animationInterval = setInterval(() => {
      const currentTime = Date.now();
      const elapsedTime = currentTime - startTime;
      
      // PRIMERA FASE: Primeros 60 segundos (hasta 70%)
      if (this.isFirst1min && elapsedTime < firstPhaseDuration) {
        // Calcular progreso basado en tiempo lineal
        const currentProgress = startProgress + (elapsedTime * incrementPerMsFirstPhase);
        this.progressValue = Math.min(this.maxProgress1min, currentProgress);
        this.updateProgress();
        
      }
      // TRANSICIÓN: Al llegar a los 60 segundos (1 minuto)
      else if (this.isFirst1min && elapsedTime >= firstPhaseDuration) {
        this.isFirst1min = false;
        this.oneMinuteMarkReached = true;
        this.progressValue = this.maxProgress1min; // Asegurar que esté en 70%
      }
      // SEGUNDA FASE: Después de 1 minuto (70% a 90%)
      else if (this.oneMinuteMarkReached && this.progressValue < this.maxProgressFinal) {
        // Incremento gradual: 0.5% cada 2 segundos
        const timeSince1min = elapsedTime - firstPhaseDuration;
        const secondsSince1min = timeSince1min / 1000;
        
        // Calcular incremento basado en tiempo (0.5% cada 2 segundos = 0.25% por segundo)
        const increment = (this.incrementAfter1min * secondsSince1min) / 2;
        const newProgress = this.maxProgress1min + increment;
        
        this.progressValue = Math.min(this.maxProgressFinal, newProgress);
        this.updateProgress();
        
      }
      // TERCERA FASE: Mantenerse en 90% si ya llegó
      else if (this.progressValue >= this.maxProgressFinal) {
        // Mantener en 90% sin cambios
        if (this.progressValue !== this.maxProgressFinal) {
          this.progressValue = this.maxProgressFinal;
          this.updateProgress();
        }
      }
      
      this.lastUpdateTime = elapsedTime;
      
    }, intervalTime);
  }

  stopAnimation(): void {
    if (this.animationInterval) {
      clearInterval(this.animationInterval);
      this.animationInterval = null;
    }
  }

  updateAnimationSpeed(): void {
    if (this.animateProgress) {
      // Reiniciar la animación con la nueva velocidad
      this.stopAnimation();
      this.startAnimation();
    }
  }

  updateFromEndpoint(newProgress: number): void {
    if (newProgress === 100) {
      this.completeProgress();
    } else {
      this.progressValue = Math.min(100, Math.max(0, newProgress));
      this.updateProgress();
    }
  }

}
